/**
 * Absolute-deviation measures of spread.
 *
 * Two different statistics share the abbreviation MAD, and mixing them up is the most
 * common mistake with either:
 *
 * - **Mean absolute deviation** — the average distance from a centre: Σ|x − c| / n.
 *   Around the mean is the school-maths definition; around the median it is smallest.
 * - **Median absolute deviation** — the median of |x − median|. A robust spread measure;
 *   multiplied by 1/Φ⁻¹(¾) ≈ 1.4826 it estimates σ for normally distributed data
 *   (SciPy `median_abs_deviation(scale="normal")`, R's `mad()` default).
 */

import { mean, median } from "@/lib/stats/descriptive";
import { normalQuantile } from "@/lib/stats/distributions";

/** 1 / Φ⁻¹(0.75) ≈ 1.482602218505602. */
export const MAD_NORMAL_SCALE = 1 / normalQuantile(0.75);

export interface DeviationRow {
  x: number;
  /** x − x̄. */
  fromMean: number;
  /** |x − x̄|. */
  absFromMean: number;
  /** |x − median|. */
  absFromMedian: number;
}

export interface DeviationReport {
  n: number;
  mean: number;
  median: number;
  rows: DeviationRow[];
  sumAbsFromMean: number;
  sumAbsFromMedian: number;
  /** Mean absolute deviation around the mean. */
  madAroundMean: number;
  /** Mean absolute deviation around the median. */
  madAroundMedian: number;
  /** Median of |x − median|. */
  medianAbsoluteDeviation: number;
  /** The median absolute deviation × 1.4826. */
  scaledMedianAbsoluteDeviation: number;
}

export function deviationReport(
  values: readonly number[]
): DeviationReport | null {
  const n = values.length;
  if (n === 0) {
    return null;
  }
  const m = mean(values);
  const med = median(values);
  const rows = values.map((x) => ({
    x,
    fromMean: x - m,
    absFromMean: Math.abs(x - m),
    absFromMedian: Math.abs(x - med),
  }));
  let sumAbsFromMean = 0;
  let sumAbsFromMedian = 0;
  for (const row of rows) {
    sumAbsFromMean += row.absFromMean;
    sumAbsFromMedian += row.absFromMedian;
  }
  const medianAbsoluteDeviation = median(rows.map((row) => row.absFromMedian));
  return {
    n,
    mean: m,
    median: med,
    rows,
    sumAbsFromMean,
    sumAbsFromMedian,
    madAroundMean: sumAbsFromMean / n,
    madAroundMedian: sumAbsFromMedian / n,
    medianAbsoluteDeviation,
    scaledMedianAbsoluteDeviation: medianAbsoluteDeviation * MAD_NORMAL_SCALE,
  };
}
